import { BlockStack, Button, Card, InlineGrid, Text } from "@shopify/polaris";
import { useAppDispatch } from "hooks";
import { PlusIcon } from "@shopify/polaris-icons";
import { useParams } from "react-router-dom";
import PrettyDate from "components/format/PrettyDate";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { useTranslation } from "react-i18next";

const CustomerQuestionnaire = ({
  customerId,
  email,
  forms,
}: {
  customerId: string;
  email: string | undefined;
  forms: any[];
}) => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const dispatch = useAppDispatch();

  const handleConfirmQuestionnaire = (subject: string) => {
    dispatch(setModalxComponent("QuestionnaireConfirmPopup"));
    dispatch(setModalxComponentProps({ companyId, customerId, subject }));
  };

  const latestForm = forms?.[0];
  const formKeyCreatedAt = latestForm?.formKeyCreatedAt ?? null;
  const formSignature = latestForm?.formSignature ?? null;

  return (
    <Card roundedAbove="sm">
      <BlockStack gap="200">
        <InlineGrid columns="1fr auto">
          <Text as="h2" variant="headingSm">
            {t("customer_questionnaire")}
          </Text>
          {email && (
            <>
              {(!formKeyCreatedAt || (formKeyCreatedAt && formSignature)) && (
                <Button
                  onClick={() => handleConfirmQuestionnaire("generate")}
                  accessibilityLabel={t("customer_questionnaire-generate")}
                  icon={PlusIcon as any}
                >
                  {t("customer_questionnaire-generate")}
                </Button>
              )}
              {formKeyCreatedAt && !formSignature && (
                <Button onClick={() => handleConfirmQuestionnaire("cancel")}>
                  {t("cancel_form")}
                </Button>
              )}
            </>
          )}
        </InlineGrid>
        {/* All Forms Section */}
        {forms.length > 0 && (
          <BlockStack gap="150">
            {forms.map((form, index) => (
              <Card key={form.id || index} padding="200">
                <BlockStack gap="100">
                  <Text as="p">
                    {t("customer_questionnaire-generated")}:{" "}
                    <PrettyDate
                      showTime={true}
                      dateString={form.formKeyCreatedAt}
                    />
                  </Text>
                  {form.formSignature ? (
                    <Text as="p" tone="success">
                      {t("customer_questionnaire-signed")}
                      <br />
                      {(() => {
                        // Get signer name - prefer firstName/lastName, fallback to fullName
                        let signerName = "";
                        if (
                          form.formSignature.signerFirstName ||
                          form.formSignature.signerLastName
                        ) {
                          signerName = `${
                            form.formSignature.signerFirstName || ""
                          } ${form.formSignature.signerLastName || ""}`.trim();
                        } else if (form.formSignature.signerFullName) {
                          signerName = form.formSignature.signerFullName;
                        }

                        // Combine name and position with space
                        const nameAndPosition = [
                          signerName,
                          form.formSignature.signerPosition,
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return nameAndPosition ? (
                          <>
                            {nameAndPosition}
                            <br />
                          </>
                        ) : null;
                      })()}
                      <PrettyDate
                        showTime={true}
                        dateString={form.formSignature.datetime}
                      />
                      <br />
                      {form.formSignature.ip}
                    </Text>
                  ) : (
                    <Text as="p" tone="critical">
                      {t("customer_questionnaire-not_signed")}
                    </Text>
                  )}
                </BlockStack>
              </Card>
            ))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
};

export default CustomerQuestionnaire;
